import { FC } from 'react';
import { Button, Dropdown } from 'antd';
import { TbDotsVertical, TbHistory } from 'react-icons/tb';

import { useResourceHistory } from '@/contexts/02-Authenticated';
import { useAntDesignTheme } from '@/hooks/useAntDesignTheme';
import {
    ActivityTimelineItem,
    MiddleDot,
    RelativeTimeTooltip,
    ResourceActivityActorIdentity,
    resourceActivityIcon,
} from '@/shared';
import { DropdownMenuPanel } from '@/shared/components/dropdowns/DropdownPanel';
import type { CommentComposerValue, ResourceActivity, ResourceComment } from '@/types';
import { isVersionedResourceType, ResourceActivityEventType } from '@/types';
import { dayjs } from '@/utilities';

import ResourceCommentCard from '../../../ResourceCommentCard';

interface ResourceActivityFeedItemProps {
    activity: ResourceActivity;
    activityComment?: ResourceComment;
    activityCommentReplies: ResourceComment[];
    isLastActivity: boolean;
    onCreateReply: (
        parentComment: ResourceComment,
        value: CommentComposerValue,
    ) => Promise<boolean | void> | boolean | void;
    resourceTitle?: string;
    resourceWebsite?: string;
    submittingReply: boolean;
}

const ResourceActivityFeedItem: FC<ResourceActivityFeedItemProps> = ({
    activity,
    activityComment,
    activityCommentReplies,
    isLastActivity,
    onCreateReply,
    resourceTitle,
    resourceWebsite,
    submittingReply,
}): JSX.Element => {
    const { openResourceHistory } = useResourceHistory();
    const { token } = useAntDesignTheme();
    const versionedResourceType = isVersionedResourceType(activity.resourceType) ? activity.resourceType : undefined;

    return (
        <ActivityTimelineItem
            action={
                versionedResourceType ? (
                    <Dropdown
                        popupRender={() => (
                            <DropdownMenuPanel
                                {...{
                                    items: [
                                        {
                                            icon: <TbHistory size='1rem' />,
                                            key: 'history',
                                            label: 'Show History',
                                            onClick: () =>
                                                openResourceHistory({
                                                    activityId: activity.id,
                                                    resourceId: activity.resourceId,
                                                    resourceType: versionedResourceType,
                                                    title: activity.title,
                                                }),
                                        },
                                    ],
                                }}
                            />
                        )}
                        placement='bottomRight'
                        trigger={['click']}
                    >
                        <Button
                            aria-label={`Actions for ${activity.title}`}
                            icon={<TbDotsVertical style={{ color: token.colorTextTertiary }} />}
                            size='small'
                            type='text'
                        />
                    </Dropdown>
                ) : undefined
            }
            icon={resourceActivityIcon(activity)}
            isLast={isLastActivity}
            metadata={
                <small style={{ color: token.colorTextTertiary }}>
                    {dayjs(activity.createdAt).format('MMM D, YYYY h:mm A')}
                    <MiddleDot horizontalMargin='0.375rem' />
                    <RelativeTimeTooltip date={activity.createdAt} />
                </small>
            }
            steps={
                activity.eventType === ResourceActivityEventType.COMMENTED && activityComment
                    ? [
                          {
                              content: (
                                  <ResourceCommentCard
                                      comment={activityComment}
                                      onCreateReply={onCreateReply}
                                      replies={activityCommentReplies}
                                      reportComponentsById={{}}
                                      resourceId={activityComment.resourceId}
                                      resourceTitle={resourceTitle}
                                      resourceType={activityComment.resourceType}
                                      resourceWebsite={resourceWebsite}
                                      showActions
                                      submittingReply={submittingReply}
                                  />
                              ),
                              key: `comment:${activityComment.id}`,
                          },
                      ]
                    : []
            }
            title={
                <>
                    <ResourceActivityActorIdentity activity={activity} />{' '}
                    <span style={{ color: token.colorTextSecondary }}>{activity.summary ?? activity.action}</span>
                </>
            }
        />
    );
};

export default ResourceActivityFeedItem;
